import { UserFacingError } from './userFacingError.ts';

// Cap how much of a backend error string we surface in the UI — keeps an
// unexpectedly long message from blowing out a toast / inline error.
const MAX_MESSAGE_LENGTH = 300;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

// Extracts a displayable message from a rejected axios error's backend
// envelope, falling back to the provided string when there is none.
export function apiErrorMessage(err: unknown, fallback: string): string {
    if (!isRecord(err)) return fallback;
    const response = err.response;
    if (!isRecord(response)) return fallback;
    const data = response.data;
    if (!isRecord(data)) return fallback;

    // Accept both the flat `{ message }` shape and the v2 envelope
    // `{ success: false, error: { code, message } }`.
    const candidates: unknown[] = [
        data.message,
        isRecord(data.error) ? data.error.message : undefined
    ];
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
            return candidate.trim().slice(0, MAX_MESSAGE_LENGTH);
        }
    }
    return fallback;
}

// A client-side refusal carries its own user-ready message. Only tagged errors
// qualify: an internal TypeError must never reach a toast.
export function uiErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof UserFacingError && err.message.trim().length > 0) {
        return err.message.trim().slice(0, MAX_MESSAGE_LENGTH);
    }
    return apiErrorMessage(err, fallback);
}

// HTTP status from a rejected axios error, so callers can tell "no such
// record" apart from "the request failed".
export function apiErrorStatus(err: unknown): number | null {
    if (!isRecord(err)) return null;
    const response = err.response;
    if (!isRecord(response)) return null;
    return typeof response.status === 'number' ? response.status : null;
}

// Machine-readable code from a v2 error envelope, for failures the UI words its own way.
export function apiErrorCode(err: unknown): string | null {
    if (!isRecord(err) || !isRecord(err.response)) return null;
    const data = err.response.data;
    if (!isRecord(data) || !isRecord(data.error)) return null;
    return typeof data.error.code === 'string' ? data.error.code : null;
}
